// src/components/SharePointSearch/SharePointIcons.tsx
// SVG icon components for SharePointSearch feature

import * as React from "react";
import { FILE_TYPE_COLORS } from "./sharepointSearchStyles";

/** SharePoint logo icon */
export const SharePointLogoIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="7" fill="#038387" opacity="0.9" />
    <circle cx="7" cy="14" r="5.5" fill="#038387" opacity="0.7" />
    <circle cx="12" cy="18" r="4.5" fill="#038387" opacity="0.5" />
    <text
      x="12"
      y="11"
      textAnchor="middle"
      fontSize="9"
      fontWeight="bold"
      fill="white"
      fontFamily="Segoe UI, sans-serif"
    >
      S
    </text>
  </svg>
);

/** File type icon badge — resolves from MIME type with filename fallback */
export const FileTypeIcon: React.FC<{ mimeType: string; fileName?: string }> = ({
  mimeType,
  fileName,
}) => {
  const ext = resolveFileType(mimeType, fileName);
  const color = FILE_TYPE_COLORS[ext] || FILE_TYPE_COLORS.default;
  const label = ext === "docx" ? "W" : ext === "pdf" ? "P" : "D";

  return (
    <div
      style={{
        width: "20px",
        height: "20px",
        borderRadius: "3px",
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span style={{ color: "white", fontSize: "11px", fontWeight: 700, lineHeight: 1 }}>
        {label}
      </span>
    </div>
  );
};

/** Resolve file type from MIME type with filename fallback */
function resolveFileType(mimeType: string, fileName?: string): string {
  const lower = mimeType.toLowerCase();
  if (lower.includes("word") || lower.includes("docx") || lower === "application/msword")
    return "docx";
  if (lower.includes("pdf")) return "pdf";

  // Fallback: parse from filename
  if (fileName) {
    const match = fileName.match(/\.(\w+)$/);
    if (match) {
      const fileExt = match[1].toLowerCase();
      if (fileExt === "pdf") return "pdf";
      if (fileExt === "docx" || fileExt === "doc") return "docx";
    }
  }

  return "default";
}

/** Download arrow icon for read/fetch actions */
export const DownloadIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#038387"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/** Chevron icon with rotation animation */
export const ChevronIcon: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    stroke="#6C757D"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      width: "14px",
      height: "14px",
      flexShrink: 0,
      transition: "transform 0.2s ease",
      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
    }}
  >
    <path d="M9 18L15 12L9 6" />
  </svg>
);

/** External link icon */
export const ExternalLinkIcon: React.FC = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11M15 3H21M21 3V9M21 3L10 14"
      stroke="#038387"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Lock icon for permission errors */
export const LockIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    stroke="#721c24"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: "1px" }}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/** Clock icon for timeout errors */
export const ClockIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    stroke="#856404"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: "1px" }}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/** Info circle icon for general errors */
export const InfoCircleIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    stroke="#721c24"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: "1px" }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

/** Warning triangle icon */
export const WarningIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    stroke="#856404"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: "1px" }}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
